import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '../../../api/client';
import { CustomHeadersEditor, type CustomHeaderEntry } from '../../../components/CustomHeadersEditor';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { AlertsSection } from '../components/AlertsSection';
import { BrandingSection } from '../components/BrandingSection';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { useToastStore } from '../../../stores/toastStore';
import { ConfirmDialog } from '../../schema/components/ConfirmDialog';
import { useDocumentTypes } from '../../schema/hooks/useDocumentTypes';
import {
  addProvider,
  ConfigQueryKeys,
  createWebhook,
  deleteProvider,
  deleteWebhook,
  getLicenseStatus,
  getProviderTypeMeta,
  listProviders,
  listWebhooks,
  PROVIDER_MAX_TOKENS_CEILING,
  PROVIDER_MAX_TOKENS_DEFAULTS,
  PROVIDER_TYPE_OPTIONS,
  rotateWebhookSecret,
  testProvider,
  testWebhook,
  updateProvider,
  updateWebhook,
  type AddModelProviderRequest,
  type ConfigLicenseStatusResponse,
  type CreateWebhookSubscriptionRequest,
  type CreateWebhookSubscriptionResponse,
  type ModelProviderResponse,
  type RotateWebhookSecretResponse,
  type TestModelProviderResponse,
  type UpdateModelProviderRequest,
  type WebhookEventName,
  type WebhookSubscriptionResponse,
  WEBHOOK_EVENTS,
  WEBHOOK_EVENT_LABELS,
} from '../api';
import { EditProviderModal } from '../components/EditProviderModal';
import { EditWebhookModal } from '../components/EditWebhookModal';

type Tab = 'providers' | 'webhooks' | 'alerts' | 'branding' | 'license';

const TAB_LABEL: Record<Tab, string> = {
  providers: 'Model providers',
  webhooks: 'Webhooks',
  alerts: 'Alertas',
  branding: 'Marca',
  license: 'Licencia',
};

const TABS: ReadonlyArray<Tab> = ['providers', 'webhooks', 'alerts', 'branding', 'license'];

/**
 * Configuración del tenant. Tres secciones gobernadas por `?tab=`:
 *   - providers: alta/listado/test/borrado de model providers (con guard 403 Starter+Ollama).
 *   - webhooks: alta (secret one-shot), listado, test, rotate-secret, borrado.
 *   - license: card de uso vs límite + features del plan.
 *
 * Densa por necesidad — el patrón decomposiciones-en-componentes lo aplica un
 * pulido futuro cuando vuelva la quota de subagentes.
 */
export function ConfigOverviewPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const active: Tab = TABS.find((t) => t === tabParam) ?? 'providers';

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Configuración</h1>
        <p className="mt-1 text-sm text-slate-500">
          Providers de IA, webhooks de notificación y resumen de licencia del tenant.
        </p>
      </header>

      <nav className="flex gap-2 border-b border-slate-200">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setSearchParams({ tab })}
            className={clsx(
              'px-3 py-2 text-sm font-medium transition-colors',
              tab === active
                ? 'border-b-2 border-primary text-primary'
                : 'text-slate-600 hover:text-slate-900',
            )}
          >
            {TAB_LABEL[tab]}
          </button>
        ))}
      </nav>

      {active === 'providers' ? <ProvidersSection /> : null}
      {active === 'webhooks' ? <WebhooksSection /> : null}
      {active === 'alerts' ? <AlertsSection /> : null}
      {active === 'branding' ? <BrandingSection /> : null}
      {active === 'license' ? <LicenseSection /> : null}
    </div>
  );
}

// ============================================================================
// PROVIDERS
// ============================================================================

function ProvidersSection(): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);

  const licenseStatus = useQuery({
    queryKey: ConfigQueryKeys.license,
    queryFn: ({ signal }) => getLicenseStatus(signal),
  });

  const providers = useQuery({
    queryKey: ConfigQueryKeys.providers,
    queryFn: ({ signal }) => listProviders(signal),
  });

  const createMutation = useMutation({
    mutationFn: addProvider,
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Provider añadido.' });
      void queryClient.invalidateQueries({ queryKey: ConfigQueryKeys.providers });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo añadir el provider', pushToast),
  });

  const deleteMutation = useMutation({
    mutationFn: (providerId: string) => deleteProvider(providerId),
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Provider eliminado.' });
      void queryClient.invalidateQueries({ queryKey: ConfigQueryKeys.providers });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo eliminar', pushToast),
  });

  const updateMutation = useMutation({
    mutationFn: (request: UpdateModelProviderRequest) => updateProvider(request),
    onSuccess: (_, request) => {
      const rotated = request.newApiKeyPlaintext != null && request.newApiKeyPlaintext !== '';
      pushToast({
        tone: 'success',
        title: rotated ? 'Provider actualizado y secret rotado.' : 'Provider actualizado.',
      });
      void queryClient.invalidateQueries({ queryKey: ConfigQueryKeys.providers });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo actualizar el provider', pushToast),
  });

  const allowsLocal = licenseStatus.data?.features.allowsLocalAiProviders ?? false;
  const [showForm, setShowForm] = useState(false);
  const [providerIdToDelete, setProviderIdToDelete] = useState<string | null>(null);
  const [editingProvider, setEditingProvider] = useState<ModelProviderResponse | null>(null);

  const handleConfirmDelete = (): void => {
    if (providerIdToDelete === null) return;
    const id = providerIdToDelete;
    deleteMutation.mutate(id, {
      onSettled: () => setProviderIdToDelete(null),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Providers configurados</h2>
        <button type="button" className="btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancelar' : '+ Añadir provider'}
        </button>
      </div>

      {licenseStatus.data !== undefined && !allowsLocal ? (
        <UpgradePrompt feature="Los providers de IA locales (Ollama / relay)" requiredPlan="Professional" />
      ) : null}

      {showForm ? (
        <ProviderForm
          allowsLocal={allowsLocal}
          busy={createMutation.isPending}
          onSubmit={(req) => createMutation.mutate(req, { onSuccess: () => setShowForm(false) })}
        />
      ) : null}

      {providers.isLoading ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      ) : providers.isError ? (
        <ErrorCard message="No se pudieron cargar los providers." />
      ) : (providers.data ?? []).length === 0 ? (
        <EmptyCard message="Aún no has configurado ningún provider de IA." />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Alias</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Modelo</th>
                <th className="px-4 py-3">Tokens (extr. / disc.)</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(providers.data ?? []).map((p) => (
                <ProviderRow
                  key={p.providerId}
                  provider={p}
                  onEdit={() => setEditingProvider(p)}
                  onDelete={() => setProviderIdToDelete(p.providerId)}
                  deleting={deleteMutation.isPending && deleteMutation.variables === p.providerId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={providerIdToDelete !== null}
        tone="danger"
        title={`¿Eliminar proveedor "${providerIdToDelete ?? ''}"?`}
        description={
          <span>
            Esta acción borra el proveedor del tenant. Los tipos de documento que aún lo
            referencien dejarán de poder ejecutar nuevos jobs hasta que los reasignes a
            otro proveedor. Los documentos ya procesados no se ven afectados.
          </span>
        }
        confirmLabel="Eliminar proveedor"
        busy={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setProviderIdToDelete(null)}
      />

      <EditProviderModal
        provider={editingProvider}
        busy={updateMutation.isPending}
        onClose={() => setEditingProvider(null)}
        onSubmit={(request) =>
          updateMutation.mutate(request, {
            onSuccess: () => setEditingProvider(null),
          })
        }
      />
    </div>
  );
}

function ProviderForm({
  allowsLocal,
  busy,
  onSubmit,
}: {
  allowsLocal: boolean;
  busy: boolean;
  onSubmit: (req: AddModelProviderRequest) => void;
}): JSX.Element {
  const { register, handleSubmit, watch, formState } = useForm<AddModelProviderRequest>({
    defaultValues: { type: 'AnthropicCloud', alias: '' },
  });
  const type = watch('type');
  const meta = getProviderTypeMeta(type);
  const [headers, setHeaders] = useState<CustomHeaderEntry[]>([]);

  return (
    <form
      onSubmit={handleSubmit((values) => {
        const customHeaders = headers
          .filter((h) => h.name.trim() !== '' && h.value !== '')
          .map((h) => ({ name: h.name.trim(), value: h.value, isSecret: h.isSecret }));
        onSubmit({
          ...values,
          ...(customHeaders.length > 0 ? { customHeaders } : {}),
        });
      })}
      className="card space-y-3 p-4"
      noValidate
    >
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label" htmlFor="provider-type">
            Tipo
          </label>
          <select id="provider-type" className="input" {...register('type', { required: true })}>
            {PROVIDER_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value} disabled={!allowsLocal && option.isLocalAi}>
                {option.label}
                {!allowsLocal && option.isLocalAi ? ' (requiere Professional+)' : ''}
              </option>
            ))}
          </select>
          {type === 'ContentUnderstanding' ? (
            <p className="mt-1 text-xs text-slate-500">
              Requiere un recurso Microsoft Foundry con Content Understanding habilitado. Endpoint
              con formato <code className="font-mono">https://&lt;resource&gt;.cognitiveservices.azure.com/</code>.
            </p>
          ) : null}
          {type === 'OllamaViaAzureRelay' ? (
            <p className="mt-1 text-xs text-slate-500">
              Ollama self-hosted detrás de un Azure Service Bus Relay (Hybrid Connection). Endpoint
              con formato <code className="font-mono">relay+wss://&lt;namespace&gt;/&lt;hybrid-path&gt;?keyName=&lt;ruleName&gt;</code>.
              La API key abajo es la SAS key del relay.
            </p>
          ) : null}
        </div>
        <div>
          <label className="label" htmlFor="provider-alias">
            Alias
          </label>
          <input
            id="provider-alias"
            className="input"
            placeholder="Mi conexión Anthropic"
            {...register('alias', { required: true, minLength: 1 })}
          />
        </div>
        {meta.requiresApiKey ? (
          <div>
            <label className="label" htmlFor="provider-apikey">
              API key (no se mostrará tras guardar)
            </label>
            <input
              id="provider-apikey"
              type="password"
              className="input"
              {...register('apiKeyPlaintext', { required: meta.requiresApiKey })}
            />
          </div>
        ) : null}
        {meta.requiresEndpoint ? (
          <div>
            <label className="label" htmlFor="provider-endpoint">
              {type === 'OllamaViaAzureRelay' ? 'Relay URI' : 'Endpoint'}
            </label>
            <input
              id="provider-endpoint"
              className="input"
              placeholder={
                type === 'OllamaViaAzureRelay'
                  ? 'relay+wss://relay.servicebus.windows.net/ollama?keyName=sender'
                  : 'https://api.example.com'
              }
              {...register('endpoint', { required: meta.requiresEndpoint })}
            />
          </div>
        ) : null}
        <div>
          <label className="label" htmlFor="provider-model">
            {type === 'ContentUnderstanding' ? 'Base analyzer (opcional)' : 'Modelo / deployment'}
          </label>
          <input
            id="provider-model"
            className="input"
            placeholder={type === 'ContentUnderstanding' ? 'prebuilt-document' : ''}
            {...register('modelName')}
          />
        </div>
        {type === 'ContentUnderstanding' ? (
          <>
            <div>
              <label className="label" htmlFor="provider-cu-completion">
                Modelo completion (deployment)
              </label>
              <input
                id="provider-cu-completion"
                className="input"
                placeholder="gpt-4.1"
                {...register('completionDeployment')}
              />
            </div>
            <div>
              <label className="label" htmlFor="provider-cu-embedding">
                Modelo embedding (deployment)
              </label>
              <input
                id="provider-cu-embedding"
                className="input"
                placeholder="text-embedding-3-large"
                {...register('embeddingDeployment')}
              />
            </div>
          </>
        ) : null}
        <div>
          <label className="label" htmlFor="provider-max-extraction">
            Tokens máx. extracción
            <span className="ml-2 text-xs font-normal text-slate-500">
              Vacío = {PROVIDER_MAX_TOKENS_DEFAULTS.extraction}
            </span>
          </label>
          <input
            id="provider-max-extraction"
            type="number"
            min={1}
            max={PROVIDER_MAX_TOKENS_CEILING}
            className="input"
            placeholder={String(PROVIDER_MAX_TOKENS_DEFAULTS.extraction)}
            {...register('maxTokensForExtraction', {
              setValueAs: emptyToUndefinedAsNumber,
              min: 1,
              max: PROVIDER_MAX_TOKENS_CEILING,
            })}
          />
        </div>
        <div>
          <label className="label" htmlFor="provider-max-discovery">
            Tokens máx. discovery
            <span className="ml-2 text-xs font-normal text-slate-500">
              Vacío = {PROVIDER_MAX_TOKENS_DEFAULTS.discovery}
            </span>
          </label>
          <input
            id="provider-max-discovery"
            type="number"
            min={1}
            max={PROVIDER_MAX_TOKENS_CEILING}
            className="input"
            placeholder={String(PROVIDER_MAX_TOKENS_DEFAULTS.discovery)}
            {...register('maxTokensForDiscovery', {
              setValueAs: emptyToUndefinedAsNumber,
              min: 1,
              max: PROVIDER_MAX_TOKENS_CEILING,
            })}
          />
        </div>
      </div>
      <CustomHeadersEditor value={headers} busy={busy} onChange={setHeaders} />

      <div className="flex justify-end gap-2">
        <button type="submit" className="btn-primary" disabled={busy || !formState.isValid}>
          {busy ? 'Guardando…' : 'Guardar provider'}
        </button>
      </div>
    </form>
  );
}

// react-hook-form's `valueAsNumber` turns "" into NaN, which posts a body that
// fails server-side range checks. Coerce empties to undefined so the field is
// omitted from the JSON body — that maps to "use the activity default" on the
// backend.
function emptyToUndefinedAsNumber(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function ProviderRow({
  provider,
  onEdit,
  onDelete,
  deleting,
}: {
  provider: ModelProviderResponse;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}): JSX.Element {
  const pushToast = useToastStore((s) => s.push);
  const [testResult, setTestResult] = useState<TestModelProviderResponse | null>(null);
  const testMutation = useMutation({
    mutationFn: () => testProvider(provider.providerId),
    onSuccess: (res) => {
      setTestResult(res);
      pushToast({
        tone: res.success ? 'success' : 'error',
        title: res.success
          ? `Conexión OK (${res.latencyMs} ms)`
          : `Test fallido: ${res.errorMessage ?? 'sin detalles'}`,
      });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo lanzar el test', pushToast),
  });

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3 font-medium text-slate-900">{provider.alias}</td>
      <td className="px-4 py-3 text-slate-700">{provider.type}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-600">
        {provider.modelName ?? <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-600">
        <span className={clsx(provider.maxTokensForExtraction == null && 'text-slate-400')}>
          {provider.maxTokensForExtraction ?? `${PROVIDER_MAX_TOKENS_DEFAULTS.extraction} (def.)`}
        </span>
        <span className="mx-1 text-slate-400">/</span>
        <span className={clsx(provider.maxTokensForDiscovery == null && 'text-slate-400')}>
          {provider.maxTokensForDiscovery ?? `${PROVIDER_MAX_TOKENS_DEFAULTS.discovery} (def.)`}
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={clsx(
            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
            provider.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600',
          )}
        >
          {provider.isActive ? 'Activo' : 'Inactivo'}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          {testResult !== null ? (
            <span
              className={clsx(
                'text-xs',
                testResult.success ? 'text-emerald-700' : 'text-rose-700',
              )}
            >
              {testResult.success ? `✓ ${testResult.latencyMs} ms` : '✗'}
            </span>
          ) : null}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending}
          >
            {testMutation.isPending ? 'Probando…' : 'Probar'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={onEdit}
          >
            Editar
          </button>
          <button
            type="button"
            className="btn-secondary text-rose-700 hover:bg-rose-50"
            onClick={onDelete}
            disabled={deleting}
          >
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// WEBHOOKS
// ============================================================================

function WebhooksSection(): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);

  const webhooks = useQuery({
    queryKey: ConfigQueryKeys.webhooks,
    queryFn: ({ signal }) => listWebhooks(signal),
  });
  const licenseStatus = useQuery({
    queryKey: ConfigQueryKeys.license,
    queryFn: ({ signal }) => getLicenseStatus(signal),
  });
  // Default true so the gate only appears once we know the plan disallows webhooks.
  const allowsWebhooks = licenseStatus.data?.features.allowsWebhooks ?? true;

  const [showForm, setShowForm] = useState(false);
  const [revealSecret, setRevealSecret] = useState<{ id: string; secret: string } | null>(null);
  const [editingWebhook, setEditingWebhook] = useState<WebhookSubscriptionResponse | null>(null);

  const createMutation = useMutation({
    mutationFn: createWebhook,
    onSuccess: (res: CreateWebhookSubscriptionResponse) => {
      setRevealSecret({ id: res.id, secret: res.secret });
      setShowForm(false);
      void queryClient.invalidateQueries({ queryKey: ConfigQueryKeys.webhooks });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo crear el webhook', pushToast),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWebhook(id),
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Webhook eliminado.' });
      void queryClient.invalidateQueries({ queryKey: ConfigQueryKeys.webhooks });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo eliminar', pushToast),
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => rotateWebhookSecret(id),
    onSuccess: (res: RotateWebhookSecretResponse) => {
      setRevealSecret({ id: res.id, secret: res.secret });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo rotar el secret', pushToast),
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => testWebhook(id),
    onSuccess: (res) =>
      pushToast({
        tone: res.deliveryEnqueued ? 'success' : 'warning',
        title: res.message,
      }),
    onError: (err) => surfaceApiError(err, 'No se pudo lanzar el test', pushToast),
  });

  // Reactivate a subscription the dispatcher auto-suspended after consecutive delivery failures.
  // The backend already accepts isActive on PUT; this just wires the missing UI affordance. The
  // first successful delivery after reactivation resets the failure counter, so fix the receiver
  // (return 2xx) before reactivating — otherwise the next failed test re-suspends it.
  const activateMutation = useMutation({
    mutationFn: (id: string) => updateWebhook({ id, isActive: true }),
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Webhook reactivado.' });
      void queryClient.invalidateQueries({ queryKey: ConfigQueryKeys.webhooks });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo activar el webhook', pushToast),
  });

  const updateMutation = useMutation({
    mutationFn: updateWebhook,
    onSuccess: () => {
      pushToast({ tone: 'success', title: 'Webhook actualizado.' });
      setEditingWebhook(null);
      void queryClient.invalidateQueries({ queryKey: ConfigQueryKeys.webhooks });
    },
    onError: (err) => surfaceApiError(err, 'No se pudo actualizar el webhook', pushToast),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Webhooks configurados</h2>
        <button type="button" className="btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancelar' : '+ Nuevo webhook'}
        </button>
      </div>

      {!allowsWebhooks ? <UpgradePrompt feature="Webhooks" requiredPlan="Professional" /> : null}

      {showForm ? (
        <WebhookForm
          busy={createMutation.isPending}
          onSubmit={(req) => createMutation.mutate(req)}
        />
      ) : null}

      {revealSecret !== null ? (
        <SecretReveal
          id={revealSecret.id}
          secret={revealSecret.secret}
          onClose={() => setRevealSecret(null)}
        />
      ) : null}

      {webhooks.isLoading ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      ) : webhooks.isError ? (
        <ErrorCard message="No se pudieron cargar los webhooks." />
      ) : (webhooks.data ?? []).length === 0 ? (
        <EmptyCard message="Aún no has configurado ningún webhook." />
      ) : (
        <div className="space-y-2">
          {(webhooks.data ?? []).map((w) => (
            <WebhookCard
              key={w.id}
              webhook={w}
              onEdit={() => setEditingWebhook(w)}
              onDelete={() => deleteMutation.mutate(w.id)}
              onTest={() => testMutation.mutate(w.id)}
              onRotate={() => rotateMutation.mutate(w.id)}
              onActivate={() => activateMutation.mutate(w.id)}
              busyDelete={deleteMutation.isPending && deleteMutation.variables === w.id}
              busyTest={testMutation.isPending && testMutation.variables === w.id}
              busyRotate={rotateMutation.isPending && rotateMutation.variables === w.id}
              busyActivate={activateMutation.isPending && activateMutation.variables === w.id}
            />
          ))}
        </div>
      )}

      <EditWebhookModal
        webhook={editingWebhook}
        busy={updateMutation.isPending}
        onClose={() => setEditingWebhook(null)}
        onSubmit={(req) => updateMutation.mutate(req)}
      />
    </div>
  );
}

function WebhookForm({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (req: CreateWebhookSubscriptionRequest) => void;
}): JSX.Element {
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<Set<WebhookEventName>>(new Set(['document.completed']));
  // Empty set = "all document types" (the default and historical behaviour).
  const [typeSlugs, setTypeSlugs] = useState<Set<string>>(new Set());

  const documentTypes = useDocumentTypes();

  const toggle = (ev: WebhookEventName): void => {
    setEvents((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) {
        next.delete(ev);
      } else {
        next.add(ev);
      }
      return next;
    });
  };

  const toggleType = (slug: string): void => {
    setTypeSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  const valid = url.startsWith('https://') && events.size > 0;

  return (
    <form
      className="card space-y-3 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) {
          return;
        }
        // Empty selection → omit the field so the backend keeps the "all types" semantics.
        onSubmit({
          url,
          events: Array.from(events),
          ...(typeSlugs.size > 0 ? { documentTypeSlugs: Array.from(typeSlugs) } : {}),
        });
      }}
    >
      <div>
        <label className="label" htmlFor="wh-url">
          URL (https obligatorio)
        </label>
        <input
          id="wh-url"
          className="input"
          placeholder="https://hooks.example.com/docusense"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>
      <fieldset>
        <legend className="label">Eventos suscritos</legend>
        <div className="flex flex-wrap gap-3">
          {WEBHOOK_EVENTS.map((ev) => (
            <label key={ev} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={events.has(ev)} onChange={() => toggle(ev)} />
              {WEBHOOK_EVENT_LABELS[ev]}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="label">Tipos de documento</legend>
        <p className="mb-2 text-xs text-slate-500">
          Sin selección = todos los tipos. Marca tipos concretos para recibir solo esos.
        </p>
        {documentTypes.isLoading ? (
          <p className="text-sm text-slate-400">Cargando tipos…</p>
        ) : (documentTypes.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-400">No hay tipos de documento configurados.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {(documentTypes.data ?? []).map((dt) => (
              <label key={dt.slug} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={typeSlugs.has(dt.slug)}
                  onChange={() => toggleType(dt.slug)}
                />
                {dt.name}
                <span className="font-mono text-xs text-slate-400">{dt.slug}</span>
              </label>
            ))}
          </div>
        )}
      </fieldset>
      <div className="flex justify-end gap-2">
        <button type="submit" className="btn-primary" disabled={busy || !valid}>
          {busy ? 'Creando…' : 'Crear webhook'}
        </button>
      </div>
    </form>
  );
}

function WebhookCard({
  webhook,
  onEdit,
  onDelete,
  onTest,
  onRotate,
  onActivate,
  busyDelete,
  busyTest,
  busyRotate,
  busyActivate,
}: {
  webhook: WebhookSubscriptionResponse;
  onEdit: () => void;
  onDelete: () => void;
  onTest: () => void;
  onRotate: () => void;
  onActivate: () => void;
  busyDelete: boolean;
  busyTest: boolean;
  busyRotate: boolean;
  busyActivate: boolean;
}): JSX.Element {
  return (
    <div className="card flex flex-wrap items-start justify-between gap-3 p-4">
      <div className="min-w-0">
        <p className="truncate font-mono text-sm text-slate-900" title={webhook.url}>
          {webhook.url}
        </p>
        <p className="mt-1 flex flex-wrap gap-1 text-xs text-slate-500">
          {webhook.events.map((ev) => (
            <span
              key={ev}
              className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-700"
            >
              {ev}
            </span>
          ))}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-500">
          <span className="text-slate-400">Tipos:</span>
          {webhook.documentTypeSlugs.length === 0 ? (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
              Todos
            </span>
          ) : (
            webhook.documentTypeSlugs.map((slug) => (
              <span
                key={slug}
                className="rounded-full bg-indigo-50 px-2 py-0.5 font-mono font-medium text-indigo-700"
              >
                {slug}
              </span>
            ))
          )}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Estado: {webhook.isActive ? 'Activo' : 'Inactivo'}
          {webhook.lastDeliveryAt !== undefined && webhook.lastDeliveryAt !== null
            ? ` · Último delivery: ${new Date(webhook.lastDeliveryAt).toLocaleString('es-ES')} (${webhook.lastDeliveryStatus ?? '—'})`
            : null}
        </p>
      </div>
      <div className="flex gap-2">
        {!webhook.isActive ? (
          <button type="button" className="btn-primary" onClick={onActivate} disabled={busyActivate}>
            {busyActivate ? 'Activando…' : 'Activar'}
          </button>
        ) : null}
        <button type="button" className="btn-secondary" onClick={onEdit}>
          Editar
        </button>
        <button type="button" className="btn-secondary" onClick={onTest} disabled={busyTest}>
          {busyTest ? 'Probando…' : 'Probar'}
        </button>
        <button type="button" className="btn-secondary" onClick={onRotate} disabled={busyRotate}>
          {busyRotate ? 'Rotando…' : 'Rotar secret'}
        </button>
        <button
          type="button"
          className="btn-secondary text-rose-700 hover:bg-rose-50"
          onClick={onDelete}
          disabled={busyDelete}
        >
          {busyDelete ? 'Eliminando…' : 'Eliminar'}
        </button>
      </div>
    </div>
  );
}

function SecretReveal({
  id,
  secret,
  onClose,
}: {
  id: string;
  secret: string;
  onClose: () => void;
}): JSX.Element {
  const [acknowledged, setAcknowledged] = useState(false);
  const pushToast = useToastStore((s) => s.push);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(secret);
      pushToast({ tone: 'success', title: 'Secret copiado al portapapeles.' });
    } catch {
      pushToast({ tone: 'warning', title: 'No se pudo copiar; selecciónalo manualmente.' });
    }
  };

  return (
    <div className="card border-amber-300 bg-amber-50 p-4">
      <h3 className="text-sm font-semibold text-amber-900">Secret HMAC — guárdalo ahora</h3>
      <p className="mt-1 text-xs text-amber-800">
        Este valor se muestra una sola vez. Si lo pierdes, puedes rotarlo, pero no recuperarlo.
      </p>
      <p className="mt-2 text-xs text-amber-800">
        Webhook id: <span className="font-mono">{id}</span>
      </p>
      <pre className="mt-3 overflow-x-auto rounded bg-white p-2 font-mono text-xs">{secret}</pre>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" className="btn-secondary" onClick={() => void copy()}>
          Copiar
        </button>
        <label className="flex items-center gap-2 text-xs text-amber-900">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          He guardado el secret en un lugar seguro
        </label>
        <button
          type="button"
          className="btn-primary"
          onClick={onClose}
          disabled={!acknowledged}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// LICENSE
// ============================================================================

function LicenseSection(): JSX.Element {
  const status = useQuery({
    queryKey: ConfigQueryKeys.license,
    queryFn: ({ signal }) => getLicenseStatus(signal),
  });

  if (status.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  if (status.isError || status.data === undefined) {
    return <ErrorCard message="No se pudo cargar el estado de licencia." />;
  }

  return <LicenseCard data={status.data} />;
}

function LicenseCard({ data }: { data: ConfigLicenseStatusResponse }): JSX.Element {
  const ratio =
    data.documentsLimit === null
      ? 0
      : data.documentsUsed / Math.max(1, data.documentsLimit);
  const tone =
    ratio >= 0.95 ? 'bg-rose-500' : ratio >= 0.8 ? 'bg-amber-500' : 'bg-emerald-500';
  const planTone =
    data.licenseType === 'Enterprise'
      ? 'bg-emerald-100 text-emerald-800'
      : data.licenseType === 'Professional'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-slate-100 text-slate-700';

  const resetDate = new Date(data.resetsAt);

  return (
    <div className="card space-y-5 p-6">
      <div className="flex items-center justify-between">
        <span
          className={clsx('rounded-full px-3 py-1 text-sm font-semibold', planTone)}
        >
          Plan {data.licenseType}
        </span>
        <span className="text-xs text-slate-500">
          Renueva el {Number.isNaN(resetDate.getTime()) ? data.resetsAt : resetDate.toLocaleDateString('es-ES')}
        </span>
      </div>

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Uso del mes
        </p>
        <p className="mt-1 text-3xl font-semibold tabular-nums text-slate-900">
          {data.documentsUsed.toLocaleString('es-ES')}
          <span className="text-base font-normal text-slate-500">
            {' '}
            /{' '}
            {data.documentsLimit === null
              ? '∞'
              : data.documentsLimit.toLocaleString('es-ES')}{' '}
            documentos
          </span>
        </p>
        {data.documentsLimit !== null ? (
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className={clsx('h-full', tone)}
              style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }}
            />
          </div>
        ) : null}
      </div>

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Features</p>
        <ul className="mt-2 flex flex-wrap gap-2 text-xs">
          <Chip>
            Tipos:{' '}
            {data.features.documentTypesLimit === null
              ? 'ilimitado'
              : String(data.features.documentTypesLimit)}
          </Chip>
          <Chip>{data.features.allowsWebhooks ? 'Webhooks ✓' : 'Webhooks ✗'}</Chip>
          <Chip>
            {data.features.allowsLocalAiProviders ? 'Providers locales ✓' : 'Providers locales ✗'}
          </Chip>
          <Chip>Retención: {data.features.jobRetention}</Chip>
        </ul>
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }): JSX.Element {
  return <li className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{children}</li>;
}

// ============================================================================
// SHARED HELPERS
// ============================================================================

function ErrorCard({ message }: { message: string }): JSX.Element {
  return (
    <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{message}</div>
  );
}

function EmptyCard({ message }: { message: string }): JSX.Element {
  return (
    <div className="card p-6 text-center text-sm text-slate-500">{message}</div>
  );
}

function surfaceApiError(
  err: unknown,
  title: string,
  pushToast: ReturnType<typeof useToastStore.getState>['push'],
): void {
  if (err instanceof ApiError) {
    if (err.detail.code === 'license_feature_unavailable') {
      pushToast({
        tone: 'error',
        title: 'Función no disponible en tu plan',
        description: err.message,
      });
      return;
    }
    pushToast({ tone: 'error', title, description: err.message });
    return;
  }
  if (err instanceof Error) {
    pushToast({ tone: 'error', title, description: err.message });
    return;
  }
  pushToast({ tone: 'error', title, description: 'Error desconocido.' });
}
